<template>
  <div>
    <template v-if="$vuetify.breakpoint.xsOnly || isMobileApp">
      <v-text-field
        :value="numbers.join('')"
        solo
        flat
        :error="error"
        :class="{ 'is-valid': valid }"
        inputmode="numeric"
        :max-length="length"
        autocomplete="one-time-code"
        @input="updateValue"
        @focus="handleFocus"
      />
    </template>
    <template v-else>
      <form class="otc" name="one-time-code" action="#">
        <fieldset class="pa-0 ma-0">
          <div class="d-flex ml-n1 mr-n1">
            <input
              v-for="n in length"
              :id="`otc-${n}`"
              :key="`num-${n}`"
              data-cy="codeInput"
              :value="numbers[n - 1]"
              :autocomplete="n === 1 ? 'one-time-code' : undefined"
              :autofocus="n === 1"
              :data-n="n"
              :maxlength="n !== 1 ? 1 : undefined"
              class="ma-1 py-3 flex-grow-1 flex-shrink-1 text-center"
              :class="{ 'is-valid': valid, 'is-invalid': error, 'border-black': borderBlack }"
              type="number"
              @focus="handleFocus"
              @input="updateValue"
              @keyup="onKeyUp"
              @keydown="onKeyDown"
              @focusin="$emit('focusinCode')"
            />
          </div>
        </fieldset>
      </form>
    </template>
  </div>
</template>

<script>
export default {
  name: 'CodeInput',
  model: {
    prop: 'value',
    event: 'update'
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    error: {
      type: Boolean,
      default: false
    },
    valid: {
      type: Boolean,
      default: false
    },
    length: {
      type: Number,
      default: 6
    },
    borderBlack: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data(vm) {
    return {
      numbers: new Array(vm.length)
    };
  },
  watch: {
    value(newValue) {
      this.numbers = Array.from(newValue);
    }
  },
  mounted() {
    if (this.value) {
      this.numbers = Array.from(this.value);
    }
  },
  methods: {
    handleFocus(event) {
      if (!event || !event.target || typeof event.target.scrollIntoView !== 'function') {
        return;
      }
      setTimeout(() => {
        event.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    },
    onKeyUp(event) {
      if (
        event.keyCode === 16 ||
        event.keyCode === 9 ||
        event.keyCode === 224 ||
        event.keyCode === 18 ||
        event.keyCode === 17
      ) {
        return;
      }

      if (
        (event.keyCode === this.keyCodes.backspace || event.keyCode === this.keyCodes.left) &&
        event.target.previousElementSibling &&
        event.target.previousElementSibling.tagName === 'INPUT'
      ) {
        event.target.previousElementSibling.select();
      } else if (
        event.keyCode !== this.keyCodes.backspace &&
        !isNaN(parseInt(event.key)) &&
        event.target.nextElementSibling
      ) {
        event.target.nextElementSibling.select();
      }
    },
    onKeyDown(event) {
      const isDirectional = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(event.code);
      const isModifier = ['Meta', 'Alt', 'Control', 'Shift'].includes(event.key);
      const isPasteSecondKey = event.key.toLowerCase() === 'v';
      const isNumber = !isNaN(Number(event.key));
      const isNotSpace = event.code !== 'Space';
      if (isDirectional || (isNumber && isNotSpace) || isModifier || isPasteSecondKey) {
        return;
      }
      event.preventDefault();
    },
    updateValue(event) {
      let currentValueLength;

      if (typeof event === 'string') {
        this.numbers = Array.from(event);
        currentValueLength = event.length;
      } else {
        this.$emit('keyup', event);
        const data = event.data || event?.target?.value;
        currentValueLength = parseInt(event.target.dataset.n);

        if (!data) {
          this.numbers[currentValueLength - 1] = ' ';
        } else if (data.length === 1) {
          this.numbers[currentValueLength - 1] = data;
        } else {
          this.numbers = Array.from(data);
        }
      }

      const ret = this.numbers.join('');
      this.$emit('update', ret);
      this.$forceUpdate();

      const spacelessValue = ret.replace(/\s/g, '');
      if (currentValueLength === this.length || spacelessValue.length === this.length) {
        this.$emit('finish');
      }
    }
  }
};
</script>

<style scoped lang="scss">
.otc fieldset {
  border: 0;

  input {
    border: $border-width solid $grey;
    border-radius: map-get($rounded, 'sm');
    flex-basis: 32px;
    width: 100%;
    max-width: 100%;
    -moz-appearance: textfield;

    &:hover {
      border-color: $grey-darken;
    }

    &:focus,
    &:active {
      border-color: $primary;
      outline: none;
    }

    &.is-valid {
      border-color: $success;
    }

    &.is-invalid {
      border-color: $error;
    }
  }
  .border-black {
    border: 1px solid black;
  }
}
</style>
